import { ExternalLink, Loader2, Power, Puzzle, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { getApplicationIcon } from '../../apps/applications';
import toast from '../Toast';
import { api, type ManagedApplication } from '../../services/api';
import { clearFeatureFlagsCache } from '../../hooks/useFeatureFlags';

interface ApplicationsTabProps {
  applications: ManagedApplication[];
  loading: boolean;
  onApplicationUpdated: (application: ManagedApplication) => void;
  onRefresh: () => void;
}

export default function ApplicationsTab({ applications, loading, onApplicationUpdated, onRefresh }: ApplicationsTabProps) {
  const [updatingAppId, setUpdatingAppId] = useState<string | null>(null);

  const handleToggle = async (application: ManagedApplication) => {
    const enabled = !application.enabled;
    setUpdatingAppId(application.appId);

    try {
      const { data } = await api.updateApplicationEnabled(application.appId, enabled);
      onApplicationUpdated(data);
      clearFeatureFlagsCache();
      toast.success(`${data.title}已${data.enabled ? '启用' : '停用'}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新应用状态失败');
    } finally {
      setUpdatingAppId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl border border-light-border bg-white py-16 text-sm text-light-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        正在加载应用目录...
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-light-border bg-white p-10 text-center">
        <h3 className="text-lg font-medium text-light-text">应用目录加载失败或为空</h3>
        <p className="mt-2 text-sm text-light-muted">请刷新重试；若持续为空，请检查后端应用注册表配置。</p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-accent-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-700">
        应用以受控内部插件的方式管理。停用后，普通用户无法在应用中心发现或访问对应应用；管理员仍可进入，用于维护与排查。
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {applications.map((application) => {
          const Icon = getApplicationIcon(application.appId);
          const isUpdating = updatingAppId === application.appId;

          return (
            <div key={application.appId} className="rounded-2xl border border-light-border bg-white p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-accent-blue">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-light-text">{application.title}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${application.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {application.enabled ? '已启用' : '已停用'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-light-muted">{application.appId}</p>
                  <p className="mt-3 text-sm leading-6 text-light-muted">{application.description}</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-light-border pt-4">
                <div className="flex items-center gap-2 text-xs text-light-muted">
                  <Puzzle className="h-4 w-4 text-accent-blue" />
                  <span>{application.capabilities.join(' · ') || '无平台能力依赖'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {application.adminPath && (
                    <a href={application.adminPath} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-accent-blue hover:bg-blue-50">
                      专属管理
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggle(application)}
                    disabled={isUpdating}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                      application.enabled ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                    {application.enabled ? '停用应用' : '启用应用'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
